import * as React from 'react';

import {useMutation} from '@apollo/react-hooks';

import type {
    CreateNewsletter as CreateNewsletterMutation,
    CreateNewsletterVariables as CreateNewsletterMutationVariables,
    DeleteNewsletter as DeleteNewsletterMutation,
    DeleteNewsletterVariables as DeleteNewsletterMutationVariables,
    UpdateNewsletter as UpdateNewsletterMutation,
    UpdateNewsletterVariables as UpdateNewsletterMutationVariables,
    RestoreNewsletter as RestoreNewsletterMutation,
    RestoreNewsletterVariables as RestoreNewsletterMutationVariables,
    GetNewsletters as GetNewslettersQuery,
    GetNewslettersVariables as GetNewslettersQueryVariables,
    CampaignUpdateInput,
} from 'nutshell-graphql-types';

import CREATE_NEWSLETTER from './graphql/mutations/create-newsletter.graphql';
import DELETE_NEWSLETTER from './graphql/mutations/delete-newsletter.graphql';
import RESTORE_NEWSLETTER from './graphql/mutations/restore-newsletter.graphql';
import UPDATE_NEWSLETTER from './graphql/mutations/update-newsletter.graphql';
import GET_NEWSLETTERS from './graphql/queries/get-newsletters.graphql';

type Props = {
    children: (arg1: {
        onCreate: (name: string, queryVariables?: GetNewslettersQueryVariables) => Promise<any>;
        onDelete: (
            newsletterId: string,
            queryVariables?: GetNewslettersQueryVariables
        ) => Promise<any>;
        onRestore: (
            newsletterId: string,
            queryVariables?: GetNewslettersQueryVariables
        ) => Promise<any>;
        onUpdate: (input: CampaignUpdateInput) => Promise<any>;
    }) => React.ReactElement;
};

export function NewsletterMutations(props: Props) {
    const [createNewsletter] = useMutation<
        CreateNewsletterMutation,
        CreateNewsletterMutationVariables
    >(CREATE_NEWSLETTER);

    const [deleteNewsletter] = useMutation<
        DeleteNewsletterMutation,
        DeleteNewsletterMutationVariables
    >(DELETE_NEWSLETTER);

    const [restoreNewsletter] = useMutation<
        RestoreNewsletterMutation,
        RestoreNewsletterMutationVariables
    >(RESTORE_NEWSLETTER);

    const [updateNewsletter] = useMutation<
        UpdateNewsletterMutation,
        UpdateNewsletterMutationVariables
    >(UPDATE_NEWSLETTER);

    return props.children({
        onCreate: (name: string, queryVariables?: GetNewslettersQueryVariables) =>
            createNewsletter({
                variables: {newsletter: {name: name}},
                update: (proxy, mutationResults) => {
                    const newNewsletter =
                        mutationResults &&
                        mutationResults.data &&
                        mutationResults.data.newsletterCreate &&
                        mutationResults.data.newsletterCreate.newsletter;

                    if (!newNewsletter) return;

                    // This will "filter" the new newsletter from the list of
                    // archived audeinces, but it's not in that list so its fine
                    newsletterDeleteRestoreUpdate(proxy, newNewsletter, false, queryVariables);
                },
            }).then(
                // Get the newsletter for any aditional promise chaining
                (mutationResults) =>
                    mutationResults &&
                    mutationResults.data &&
                    mutationResults.data.newsletterCreate &&
                    mutationResults.data.newsletterCreate.newsletter
            ),
        onRestore: (newsletterId: string, queryVariables?: GetNewslettersQueryVariables) =>
            restoreNewsletter({
                variables: {id: newsletterId},
                update: (proxy, mutationResults) => {
                    // Get the restored newsletter
                    const restoredNewsletter =
                        mutationResults &&
                        mutationResults.data &&
                        mutationResults.data.campaignRestore &&
                        mutationResults.data.campaignRestore.campaign;

                    if (!restoredNewsletter) return;

                    newsletterDeleteRestoreUpdate(proxy, restoredNewsletter, false, queryVariables);
                },
            }),
        onDelete: (newsletterId: string, queryVariables?: GetNewslettersQueryVariables) =>
            deleteNewsletter({
                variables: {id: newsletterId},
                update: (proxy, mutationResults) => {
                    // Get the archived newsletter
                    const archivedNewsletter =
                        mutationResults &&
                        mutationResults.data &&
                        mutationResults.data.campaignDelete &&
                        mutationResults.data.campaignDelete.campaign;

                    if (!archivedNewsletter) return;

                    newsletterDeleteRestoreUpdate(proxy, archivedNewsletter, true, queryVariables);
                },
            }),
        onUpdate: (input: CampaignUpdateInput) =>
            updateNewsletter({
                variables: {newsletter: input},
            }),
    });
}

/**
 * This will handle updating our local cache when we archive or restore an
 * newsletter. It correctly handles updating both the archived and unarchived
 * caches.
 *
 * @param {DataProxy} proxy The mutation update proxy
 * @param {Newsletter} newsletter The newsletter that was mutated
 * @param {boolean} isDeleting True if we are deleting the given newsletter,
 * false if we are restoring it.
 */
const newsletterDeleteRestoreUpdate = (
    // @ts-expect-error
    proxy,
    // @ts-expect-error
    newsletter,
    isDeleting: boolean,
    listQueryVariables?: GetNewslettersQueryVariables
) => {
    const queryVariables = listQueryVariables || {};

    try {
        // Update our archived cache
        // This isn't the best variable name, but its the cache we're removing
        // an newsletter from.
        const cacheResultRemoving: GetNewslettersQuery | null | undefined = proxy.readQuery({
            query: GET_NEWSLETTERS,
            variables: {...queryVariables, archived: !isDeleting},
        });

        if (cacheResultRemoving && cacheResultRemoving.newsletters) {
            proxy.writeQuery({
                query: GET_NEWSLETTERS,
                variables: {...queryVariables, archived: !isDeleting},
                data: {
                    newsletters: {
                        ...cacheResultRemoving.newsletters,
                        edges: cacheResultRemoving.newsletters.edges.filter((edge) => {
                            return edge.node.id !== newsletter.id;
                        }),
                    },
                },
            });
        }
    } catch {
        // If the above query doesn't exist in the cache, Apollo will throw an
        // error (why it can't just return null is beyond me). If that happens,
        // we need to "catch" the error. But we don't want to really do anything
        // with it. The just means we haven't performed this query yet. So, an
        // empty catch block
    }

    // Update our unarchived cache
    // Again, not the best name. We're adding an newsletter to this cache
    try {
        const cacheResultAdding: GetNewslettersQuery | null | undefined = proxy.readQuery({
            query: GET_NEWSLETTERS,
            variables: {...queryVariables, archived: isDeleting},
        });

        if (!cacheResultAdding || !cacheResultAdding.newsletters) {
            return;
        }

        proxy.writeQuery({
            query: GET_NEWSLETTERS,
            variables: {...queryVariables, archived: isDeleting},
            data: {
                newsletters: {
                    ...cacheResultAdding.newsletters,
                    edges: [
                        {
                            cursor: newsletter.id,
                            node: newsletter,
                            __typename: 'NewslettersEdge',
                        },
                    ].concat(cacheResultAdding.newsletters.edges),
                },
            },
        });
    } catch {
        // If the above query doesn't exist in the cache, Apollo will throw an
        // error (why it can't just return null is beyond me). If that happens,
        // we need to "catch" the error. But we don't want to really do anything
        // with it. The just means we haven't performed this query yet. So, an
        // empty catch block
    }
};
